/**
 * CoConnect — AdvisoryTimelineCard (v3.4.0 Enterprise Redesign)
 * ════════════════════════════════════════════════════════════════════
 * Complete UI redesign — enterprise-grade 10/10, mature, no cartoony icons.
 *
 * Features:
 * - Multi-agent collaboration panel (shows which agents contributed)
 * - Per-action detailed instructions panel (expandable — teacher mode)
 * - Confidence + cost + source agent badges (mature design)
 * - Language selector (English, French, Pidgin, Oroko, Fulfulde)
 * - Text-to-Speech (TTS) button for each action + summary
 * - High-contrast text colors (visible in light AND dark mode)
 * - Professional typography (no emojis as icons — uses SVG status indicators)
 * - Orchestrator verification badge
 * - Treatment rotation warnings
 * - Weather dependency indicators
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../services/api';
import { Spinner, Alert, StatusPill } from './ui';

interface TimelineAction {
  day_offset: number;
  date: string;
  time_window: string;
  action: string;
  threat: string | null;
  product: string | null;
  dosage: string | null;
  reason: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  weather_dependent: boolean;
  action_index?: number;
  confidence?: number;
  estimated_cost_xaf?: number | null;
  source_agent?: string | null;
  instructions?: string[];
  precautions?: string;
  // v3.4.2: Commercial product fields
  local_name?: string | null;
  where_to_buy?: string | null;
}

interface ActionConfirmation {
  action_index: number;
  status: 'done' | 'skipped' | 'partial' | 'failed';
  notes: string | null;
  confirmed_at: string;
}

interface UrgentAlert {
  severity: 'info' | 'warning' | 'critical';
  category: string;
  title: string;
  message: string;
  recommended_action: string;
  due_date: string | null;
}

interface AgentOutput {
  agent_code: string;
  success: boolean;
  output: any;
  model: string;
  duration_ms: number;
  error?: string;
}

interface Timeline {
  id?: string;
  farmer_id: string;
  generated_at: string;
  valid_for_date: string;
  summary: string;
  ai_model: string;
  timeline: TimelineAction[];
  urgent_alerts: UrgentAlert[];
  knowledge_base_refs: any[];
  cached: boolean;
  total_estimated_cost_xaf?: number;
  overall_confidence?: number;
  agent_outputs?: AgentOutput[];
  agents_consulted?: string[];
  agents_succeeded?: number;
  agents_failed?: number;
  orchestrator_verified?: boolean;
  orchestrator_notes?: string;
}

// v3.4.0: Mature priority indicators (no cartoony icons)
const priorityConfig: Record<string, { label: string; color: string; bgColor: string; borderColor: string }> = {
  critical: { label: 'CRITICAL', color: 'text-red-700 dark:text-red-300', bgColor: 'bg-red-50 dark:bg-red-900/20', borderColor: 'border-red-300 dark:border-red-700' },
  high:     { label: 'HIGH',     color: 'text-orange-700 dark:text-orange-300', bgColor: 'bg-orange-50 dark:bg-orange-900/20', borderColor: 'border-orange-300 dark:border-orange-700' },
  medium:   { label: 'MEDIUM',   color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-50 dark:bg-blue-900/20', borderColor: 'border-blue-300 dark:border-blue-700' },
  low:      { label: 'LOW',      color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-50 dark:bg-green-900/20', borderColor: 'border-green-300 dark:border-green-700' },
};

// v3.5.0: Local farmer disease/pest names (Kumba/South West Region terminology)
const threatLabels: Record<string, string> = {
  black_pod: 'Black Pod / Pourriture',
  cssv: 'Swollen Shoot (CSSV)',
  mirid: 'Capsids / Akate',
  mistletoe: 'Bird Bush / Tap-tap',
  weather: 'Weather Risk',
  harvest: 'Harvest',
  sanitation: 'Farm Cleaning',
  pruning: 'Pruning / Cutting',
  planting: 'Planting',
  other: 'General',
};

// v3.4.0: Confidence level config
function confidenceConfig(confidence: number | undefined) {
  if (confidence === undefined || confidence === null) return null;
  if (confidence >= 90) return { label: 'Very High', color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-100 dark:bg-green-900/30' };
  if (confidence >= 75) return { label: 'High', color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-100 dark:bg-blue-900/30' };
  if (confidence >= 50) return { label: 'Medium', color: 'text-amber-700 dark:text-amber-300', bgColor: 'bg-amber-100 dark:bg-amber-900/30' };
  return { label: 'Low', color: 'text-red-700 dark:text-red-300', bgColor: 'bg-red-100 dark:bg-red-900/30' };
}

// v3.4.0: Source agent config (mature labels, no emojis)
const agentConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  DISEASE:      { label: 'Disease Specialist', color: 'text-red-700 dark:text-red-300', bgColor: 'bg-red-50 dark:bg-red-900/20' },
  PEST:         { label: 'Pest Specialist', color: 'text-orange-700 dark:text-orange-300', bgColor: 'bg-orange-50 dark:bg-orange-900/20' },
  WEATHER_INTEL:{ label: 'Weather Intelligence', color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
  SOIL:         { label: 'Soil Analyst', color: 'text-amber-700 dark:text-amber-300', bgColor: 'bg-amber-50 dark:bg-amber-900/20' },
  FERTILIZER:   { label: 'Fertilizer Advisor', color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-50 dark:bg-green-900/20' },
  SATELLITE:    { label: 'Satellite Analyst', color: 'text-indigo-700 dark:text-indigo-300', bgColor: 'bg-indigo-50 dark:bg-indigo-900/20' },
  YIELD:        { label: 'Yield Predictor', color: 'text-purple-700 dark:text-purple-300', bgColor: 'bg-purple-50 dark:bg-purple-900/20' },
  AGRONOMIST:   { label: 'Agronomist', color: 'text-slate-700 dark:text-slate-300', bgColor: 'bg-slate-100 dark:bg-slate-700/30' },
  MULTI:        { label: 'Multi-Agent Consensus', color: 'text-teal-700 dark:text-teal-300', bgColor: 'bg-teal-50 dark:bg-teal-900/20' },
};

// v3.4.0: Language options
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'pcm', label: 'Pidgin' },
  { code: 'oroko', label: 'Oroko' },
  { code: 'fuv', label: 'Fulfulde' },
];

function formatXAF(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '';
  return amount.toLocaleString('fr-FR') + ' XAF';
}

function dayLabel(offset: number, dateStr: string): string {
  if (offset === 0) return 'Today';
  if (offset === 1) return 'Tomorrow';
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

// v3.4.0: Mature SVG icons (no cartoony emojis)
const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SpeakerIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

interface Props {
  farmerId?: string;
  language?: string;
}

const AdvisoryTimelineCard: React.FC<Props> = ({ farmerId, language = 'en' }) => {
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmations, setConfirmations] = useState<Record<number, ActionConfirmation>>({});
  const [confirmingIndex, setConfirmingIndex] = useState<number | null>(null);
  const [expandedAction, setExpandedAction] = useState<number | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [speakingActionIdx, setSpeakingActionIdx] = useState<number | null>(null);
  const [speakingSummary, setSpeakingSummary] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const fetchTimeline = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const url = farmerId
        ? `/advisory-timeline/farmer/${farmerId}?lang=${selectedLanguage}`
        : `/advisory-timeline/me?lang=${selectedLanguage}`;
      const res = await apiClient.get(url);
      setTimeline(res.data);
      if (res.data?.id) {
        try {
          const confRes = await apiClient.get(`/advisory-timeline/actions/${res.data.id}/confirmations`);
          const confMap: Record<number, ActionConfirmation> = {};
          (confRes.data?.confirmations || []).forEach((c: any) => {
            confMap[c.action_index] = c;
          });
          setConfirmations(confMap);
        } catch { /* non-fatal */ }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load advisory timeline');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [farmerId, selectedLanguage]);

  useEffect(() => { fetchTimeline(false); }, [fetchTimeline]);

  // v3.4.0: TTS — speak text using server-side neural TTS or browser fallback
  // v3.6.0: Map advisory language to TTS language code for browser speech synthesis
  // This ensures the voice matches the selected language (French, English, Pidgin, etc.)
  const getTtsLang = (lang: string): string => {
    const langMap: Record<string, string> = {
      'fr': 'fr-FR',      // French
      'en': 'en-US',      // English
      'pcm': 'en-US',     // Pidgin (uses English voice — closest available)
      'oroko': 'en-US',   // Oroko (uses English voice — no browser support for Oroko)
      'fuv': 'fr-FR',     // Fulfulde (uses French voice — closest available in Cameroon)
      'mok': 'en-US',     // Mokpe (uses English voice)
    };
    return langMap[lang] || 'en-US';
  };

  // v3.6.0: Try to find a matching voice from the browser's available voices
  const findBestVoice = (ttsLang: string): SpeechSynthesisVoice | null => {
    if (!('speechSynthesis' in window)) return null;
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return null;
    // Try exact match first
    const exactMatch = voices.find(v => v.lang === ttsLang);
    if (exactMatch) return exactMatch;
    // Try partial match (e.g., "en" matches "en-US", "en-GB")
    const langPrefix = ttsLang.split('-')[0];
    const partialMatch = voices.find(v => v.lang.startsWith(langPrefix));
    if (partialMatch) return partialMatch;
    // Return default voice
    return voices[0];
  };

  const speak = async (text: string, actionIdx?: number) => {
    const isSummary = actionIdx === undefined;
    if (isSummary) setSpeakingSummary(true);
    else setSpeakingActionIdx(actionIdx);

    try {
      // Try server-side TTS first (neural voice via Fish Audio)
      const res = await apiClient.post('/ai/tts', { text: text.slice(0, 4000), language: selectedLanguage });
      if (res.data?.audio_url) {
        // Stop any currently playing audio
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        const audio = new Audio(res.data.audio_url);
        audioRef.current = audio;
        audio.onended = () => {
          if (isSummary) setSpeakingSummary(false);
          else setSpeakingActionIdx(null);
        };
        audio.onerror = () => {
          if (isSummary) setSpeakingSummary(false);
          else setSpeakingActionIdx(null);
        };
        await audio.play();
        return;
      }
    } catch {
      // Fall back to browser TTS
    }

    // Browser TTS fallback — v3.6.0: use correct language code + best matching voice
    try {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        const ttsLang = getTtsLang(selectedLanguage);
        utterance.lang = ttsLang;
        // v3.6.0: Find the best matching voice for the language
        const voice = findBestVoice(ttsLang);
        if (voice) {
          utterance.voice = voice;
          console.log(`[TTS] Using voice: ${voice.name} (${voice.lang}) for language: ${selectedLanguage}`);
        }
        utterance.rate = 0.9;  // v3.6.0: slightly slower for clarity
        utterance.pitch = 1.0;
        utterance.onend = () => {
          if (isSummary) setSpeakingSummary(false);
          else setSpeakingActionIdx(null);
        };
        utterance.onerror = () => {
          if (isSummary) setSpeakingSummary(false);
          else setSpeakingActionIdx(null);
        };
        window.speechSynthesis.speak(utterance);
      }
    } catch {
      if (isSummary) setSpeakingSummary(false);
      else setSpeakingActionIdx(null);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setSpeakingSummary(false);
    setSpeakingActionIdx(null);
  };

  const confirmAction = async (actionIndex: number, status: 'done' | 'skipped' | 'partial' | 'failed') => {
    if (!timeline?.id) return;
    setConfirmingIndex(actionIndex);
    try {
      const res = await apiClient.post(`/advisory-timeline/actions/${timeline.id}/${actionIndex}/confirm`, { status });
      setConfirmations(prev => ({ ...prev, [actionIndex]: res.data.confirmation }));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to confirm action');
    } finally {
      setConfirmingIndex(null);
    }
  };

  // v3.6.0: Refresh passes the selected language so the server regenerates in that language
  const refresh = () => {
    setRefreshing(true);
    apiClient.post(`/advisory-timeline/me/refresh?lang=${selectedLanguage}`).then(res => {
      setTimeline(res.data);
      setRefreshing(false);
    }).catch(err => {
      setError(err.response?.data?.error || 'Failed to refresh');
      setRefreshing(false);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
        <span className="ml-3 text-sm text-slate-600 dark:text-slate-300">Generating your personalized multi-agent advisory...</span>
      </div>
    );
  }

  if (error) return <Alert tone="warning">{error}</Alert>;
  if (!timeline) return null;

  // Group actions by day
  const actionsByDay: Record<number, TimelineAction[]> = {};
  timeline.timeline.forEach(a => {
    if (!actionsByDay[a.day_offset]) actionsByDay[a.day_offset] = [];
    actionsByDay[a.day_offset].push(a);
  });
  const dayOffsets = Object.keys(actionsByDay).map(Number).sort((a, b) => a - b).slice(0, 7);

  return (
    <div className="space-y-5 advisory-timeline-card">
      {/* ═══ Language Selector + TTS Controls ═══ */}
      <div className="flex items-center justify-between gap-3 flex-wrap advisory-controls">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Language:</label>
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
          >
            {LANGUAGES.map(lang => <option key={lang.code} value={lang.code}>{lang.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          {(speakingSummary || speakingActionIdx !== null) && (
            <button onClick={stopSpeaking} className="text-xs px-3 py-1.5 rounded-md bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 font-medium">
              Stop Audio
            </button>
          )}
          <button
            onClick={refresh}
            disabled={refreshing}
            className="text-xs px-3 py-1.5 rounded-md bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 font-medium inline-flex items-center gap-1.5"
          >
            <RefreshIcon />
            {refreshing ? 'Regenerating...' : 'Regenerate'}
          </button>
        </div>
      </div>

      {/* ═══ Summary Banner ═══ */}
      <div className="p-5 rounded-lg border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 advisory-summary-banner">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                AI Advisory · {timeline.cached ? 'Cached' : 'Just Generated'} · {timeline.ai_model}
              </h3>
              <button
                onClick={() => speakingSummary ? stopSpeaking() : speak(timeline.summary)}
                className={`p-1.5 rounded-md ${speakingSummary ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                title={speakingSummary ? 'Stop audio' : 'Listen to summary'}
              >
                <SpeakerIcon />
              </button>
            </div>
            <p className="text-base text-slate-900 dark:text-slate-50 leading-relaxed font-medium">{timeline.summary}</p>

            {/* Aggregate metrics */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {timeline.overall_confidence !== undefined && timeline.overall_confidence !== null && (() => {
                const cc = confidenceConfig(timeline.overall_confidence);
                return cc ? (
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${cc.bgColor} ${cc.color}`}>
                    {cc.label} Confidence ({timeline.overall_confidence}%)
                  </span>
                ) : null;
              })()}
              {timeline.total_estimated_cost_xaf !== undefined && timeline.total_estimated_cost_xaf !== null && timeline.total_estimated_cost_xaf > 0 && (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                  Total Cost: {formatXAF(timeline.total_estimated_cost_xaf)}
                </span>
              )}
              <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                {timeline.timeline?.length || 0} Actions · 7 Days
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Multi-Agent Collaboration Panel ═══ */}
      {timeline.agent_outputs && timeline.agent_outputs.length > 0 && (
        <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 advisory-collaboration">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">
              Multi-Agent Collaboration · {timeline.agents_succeeded || 0}/{timeline.agent_outputs.length} Succeeded
            </h3>
            {timeline.orchestrator_verified !== undefined && (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${timeline.orchestrator_verified ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                {timeline.orchestrator_verified ? 'Orchestrator Verified' : 'Orchestrator Warning'}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {timeline.agent_outputs.map((agent, i) => {
              const ac = agentConfig[agent.agent_code] || { label: agent.agent_code, color: 'text-slate-700 dark:text-slate-300', bgColor: 'bg-slate-100 dark:bg-slate-700' };
              return (
                <span
                  key={i}
                  className={`text-xs font-medium px-2.5 py-1 rounded-md border ${agent.success ? `${ac.bgColor} ${ac.color} border-current` : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'}`}
                  title={agent.success ? `${ac.label} (${agent.model}, ${agent.duration_ms}ms)` : `${ac.label} failed: ${agent.error || 'unknown'}`}
                >
                  {ac.label} {agent.success ? '✓' : '✗'}
                </span>
              );
            })}
          </div>
          {timeline.orchestrator_notes && (
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-3 italic border-l-2 border-slate-300 dark:border-slate-600 pl-3">
              {timeline.orchestrator_notes}
            </p>
          )}
        </div>
      )}

      {/* ═══ Urgent Alerts ═══ */}
      {timeline.urgent_alerts.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Urgent Alerts</h3>
          {timeline.urgent_alerts.map((alert, i) => {
            const isCritical = alert.severity === 'critical';
            const isWarning = alert.severity === 'warning';
            return (
              <div
                key={i}
                className={`advisory-urgent-alert p-4 rounded-lg border-l-4 ${isCritical ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : isWarning ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <AlertIcon />
                    <h4 className={`text-sm font-bold ${isCritical ? 'text-red-800 dark:text-red-200' : isWarning ? 'text-amber-800 dark:text-amber-200' : 'text-blue-800 dark:text-blue-200'}`}>{alert.title}</h4>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded uppercase ${isCritical ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' : isWarning ? 'bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200' : 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'}`}>
                    {alert.severity}
                  </span>
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">{alert.message}</p>
                {alert.recommended_action && (
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    <span className="font-semibold">Recommended:</span> {alert.recommended_action}
                  </p>
                )}
                {alert.due_date && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Due: {new Date(alert.due_date).toLocaleDateString()}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ 7-Day Timeline ═══ */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-3">7-Day Action Plan</h3>

        {dayOffsets.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 italic">No actions scheduled. Check back tomorrow.</p>
        ) : (
          <div className="space-y-4">
            {dayOffsets.map(offset => {
              const dayActions = actionsByDay[offset].sort((a, b) => {
                const p = { critical: 0, high: 1, medium: 2, low: 3 };
                return p[a.priority] - p[b.priority];
              });
              const dateStr = dayActions[0]?.date || new Date(Date.now() + offset * 86400000).toISOString().slice(0, 10);
              return (
                <div key={offset} className="border-l-2 border-slate-300 dark:border-slate-600 pl-4 advisory-day-group">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                    {dayLabel(offset, dateStr)}
                  </p>
                  <div className="space-y-2">
                    {dayActions.map((a, i) => {
                      const actionIdx = a.action_index ?? dayOffsets.indexOf(offset);
                      const conf = confirmations[actionIdx];
                      const isConfirmed = !!conf;
                      const isConfirming = confirmingIndex === actionIdx;
                      const isExpanded = expandedAction === actionIdx;
                      const pc = priorityConfig[a.priority] || priorityConfig.medium;
                      const cc = confidenceConfig(a.confidence);
                      const ac = a.source_agent ? (agentConfig[a.source_agent] || null) : null;
                      const isSpeaking = speakingActionIdx === actionIdx;
                      return (
                        <div key={i} className={`advisory-action-item rounded-lg border ${isConfirmed ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50'} overflow-hidden`}>
                          {/* Action header (click to expand) */}
                          <div
                            className="p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 advisory-action-head"
                            onClick={() => setExpandedAction(isExpanded ? null : actionIdx)}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 advisory-action-meta">
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${pc.bgColor} ${pc.color}`}>{pc.label}</span>
                                  {a.threat && <span className="text-xs text-slate-500 dark:text-slate-400">{threatLabels[a.threat] || a.threat}</span>}
                                  {a.weather_dependent && <span className="text-xs text-blue-600 dark:text-blue-400" title="Weather-dependent">Weather-dependent</span>}
                                </div>
                                <p className={`text-sm font-semibold text-slate-900 dark:text-slate-50 ${isConfirmed ? 'line-through opacity-60' : ''}`}>{a.action}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                  {a.time_window}
                                  {a.product && <span> · <span className="font-medium text-slate-700 dark:text-slate-300">{a.product}</span></span>}
                                  {a.local_name && <span> (locally: <span className="italic font-medium text-slate-600 dark:text-slate-400">"{a.local_name}"</span>)</span>}
                                  {a.dosage && <span> · {a.dosage}</span>}
                                </p>
                                {a.where_to_buy && (
                                  <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5 font-medium">
                                    Available at: {a.where_to_buy}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {isSpeaking && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); stopSpeaking(); }}
                                    className="p-1.5 rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                    title="Stop audio"
                                  >
                                    <SpeakerIcon />
                                  </button>
                                )}
                                {!isSpeaking && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); speak(a.action + '. ' + a.reason, actionIdx); }}
                                    className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                                    title="Listen to this action"
                                  >
                                    <SpeakerIcon />
                                  </button>
                                )}
                                <ChevronIcon open={isExpanded} />
                              </div>
                            </div>

                            {/* Badges row */}
                            {(cc || a.estimated_cost_xaf || ac) && (
                              <div className="flex flex-wrap items-center gap-1.5 mt-2 advisory-action-badges">
                                {cc && (
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${cc.bgColor} ${cc.color}`} title="AI confidence">
                                    {cc.label} ({a.confidence}%)
                                  </span>
                                )}
                                {a.estimated_cost_xaf !== null && a.estimated_cost_xaf !== undefined && a.estimated_cost_xaf > 0 && (
                                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300" title="Estimated cost">
                                    {formatXAF(a.estimated_cost_xaf)}
                                  </span>
                                )}
                                {ac && (
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${ac.bgColor} ${ac.color}`} title="Source agent">
                                    {ac.label}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Expanded details (teacher mode — instructions + precautions) */}
                          {isExpanded && (
                            <div className="px-3 pb-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30">
                              {/* v3.4.2: Product Details (trade name + local name + where to buy + dosage) */}
                              {a.product && (
                                <div className="mt-3 p-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700">
                                  <h5 className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-2">Product Details</h5>
                                  <div className="space-y-1">
                                    <p className="text-sm text-slate-800 dark:text-slate-100">
                                      <span className="font-semibold">Trade name:</span> {a.product}
                                    </p>
                                    {a.local_name && (
                                      <p className="text-sm text-slate-700 dark:text-slate-200">
                                        <span className="font-semibold">Local name:</span> <span className="italic">"{a.local_name}"</span>
                                      </p>
                                    )}
                                    {a.dosage && (
                                      <p className="text-sm text-slate-700 dark:text-slate-200">
                                        <span className="font-semibold">Dosage:</span> {a.dosage}
                                      </p>
                                    )}
                                    {a.where_to_buy && (
                                      <p className="text-sm text-emerald-800 dark:text-emerald-200">
                                        <span className="font-semibold">Where to buy:</span> {a.where_to_buy}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Reason (WHY) */}
                              <div className="mt-3">
                                <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Why This Action</h5>
                                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{a.reason}</p>
                              </div>

                              {/* Instructions (HOW) */}
                              {a.instructions && a.instructions.length > 0 && (
                                <div className="mt-3">
                                  <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Step-by-Step Instructions</h5>
                                  <ol className="space-y-1.5">
                                    {a.instructions.map((step, si) => (
                                      <li key={si} className="text-sm text-slate-700 dark:text-slate-300 flex gap-2">
                                        <span className="font-semibold text-slate-500 dark:text-slate-400 shrink-0">{si + 1}.</span>
                                        <span>{step}</span>
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              )}

                              {/* Precautions */}
                              {a.precautions && (
                                <div className="mt-3 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                                  <h5 className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-1">Precautions & Safety</h5>
                                  <p className="text-sm text-amber-800 dark:text-amber-200">{a.precautions}</p>
                                </div>
                              )}

                              {/* TTS for full details */}
                              <button
                                onClick={() => speak(`Action: ${a.action}. Why: ${a.reason}. Instructions: ${a.instructions?.join('. ') || ''}. Precautions: ${a.precautions || ''}`, actionIdx)}
                                className="mt-3 text-xs font-medium text-blue-700 dark:text-blue-300 hover:underline inline-flex items-center gap-1.5"
                              >
                                <SpeakerIcon />
                                Listen to full details
                              </button>

                              {/* Confirmation buttons */}
                              {!farmerId && (
                                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Confirm Action</p>
                                  <div className="flex items-center gap-2">
                                    {isConfirmed ? (
                                      <>
                                        <span className={`text-xs font-medium px-2.5 py-1 rounded-md ${conf.status === 'done' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : conf.status === 'skipped' ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300' : conf.status === 'partial' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                                          {conf.status === 'done' ? 'Done' : conf.status === 'skipped' ? 'Skipped' : conf.status === 'partial' ? 'Partial' : 'Failed'}
                                        </span>
                                        <span className="text-xs text-slate-400">{new Date(conf.confirmed_at).toLocaleDateString()}</span>
                                        <button onClick={() => confirmAction(actionIdx, 'done')} disabled={isConfirming} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 ml-1">undo</button>
                                      </>
                                    ) : (
                                      <>
                                        <button onClick={() => confirmAction(actionIdx, 'done')} disabled={isConfirming} className="text-xs px-2.5 py-1 rounded-md bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50 disabled:opacity-50 font-medium inline-flex items-center gap-1">
                                          <CheckIcon /> Done
                                        </button>
                                        <button onClick={() => confirmAction(actionIdx, 'skipped')} disabled={isConfirming} className="text-xs px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 font-medium">Skip</button>
                                        <button onClick={() => confirmAction(actionIdx, 'partial')} disabled={isConfirming} className="text-xs px-2.5 py-1 rounded-md bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 disabled:opacity-50 font-medium">Partial</button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ Knowledge Base Sources ═══ */}
      {timeline.knowledge_base_refs && timeline.knowledge_base_refs.length > 0 && (
        <details className="border-t border-slate-200 dark:border-slate-700 pt-3">
          <summary className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-300">
            Sources ({timeline.knowledge_base_refs.length} knowledge base documents cited)
          </summary>
          <div className="mt-2 space-y-1">
            {timeline.knowledge_base_refs.map((ref: any, i: number) => (
              <p key={i} className="text-xs text-slate-500 dark:text-slate-400">
                {ref.title}{ref.snippet ? ` — ${ref.snippet.slice(0, 80)}...` : ''}
              </p>
            ))}
          </div>
        </details>
      )}

      <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
        Generated: {new Date(timeline.generated_at).toLocaleString()} · Model: {timeline.ai_model}
      </p>
    </div>
  );
};

export default AdvisoryTimelineCard;
